<script setup lang="ts">
import { ORGANIZATION } from '~/constants/organization'

const { t, locale } = useI18n();
const { canonicalUrl } = useCanonicalUrl();

// `@nuxtjs/i18n` does not set <html lang> on its own — it only does so when
// useLocaleHead() is called, and nothing did, so every route rendered a bare
// <html>. `seo: false` is deliberate: seo:true also emits the module's own
// canonical, which would compete with useCanonicalUrl()'s (verified correct in
// docs/seo-audit-2026-08.md). We take only lang and dir here.
const localeHead = useLocaleHead({ lang: true, dir: true, seo: false });

// Applied here rather than per page so every route — including the dynamic
// category and blog ones — advertises its counterpart in the other locale.
const { alternateLinks } = useHreflang();

const config = useRuntimeConfig();
const siteUrl = (config.public.siteUrl as string).replace(/\/$/, '');

// One Organization node per page, from the layout: it describes the publisher,
// not the page, so it belongs everywhere rather than on a single route.
useJsonLd(() => ({
  '@context': 'https://schema.org',
  '@type': ['Organization', 'LocalBusiness'],
  '@id': `${siteUrl}/#organization`,
  name: ORGANIZATION.name,
  legalName: ORGANIZATION.legalName,
  url: siteUrl,
  logo: {
    '@type': 'ImageObject',
    url: `${siteUrl}${ORGANIZATION.logoPath}`,
  },
  sameAs: [...ORGANIZATION.sameAs],
  address: {
    '@type': 'PostalAddress',
    streetAddress: ORGANIZATION.address.streetAddress,
    addressLocality: ORGANIZATION.address.addressLocality,
    addressCountry: ORGANIZATION.address.addressCountry,
  },
  openingHoursSpecification: [{
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: [...ORGANIZATION.openingHours.days],
    opens: ORGANIZATION.openingHours.opens,
    closes: ORGANIZATION.openingHours.closes,
  }],
  contactPoint: [{
    '@type': 'ContactPoint',
    telephone: ORGANIZATION.telephone,
    email: ORGANIZATION.email,
    contactType: 'customer service',
    areaServed: 'BG',
    availableLanguage: ['bg', 'en'],
  }],
}), 'organization');

useJsonLd(() => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${siteUrl}/#website`,
  url: siteUrl,
  name: 'vodi4ka.com',
  publisher: { '@id': `${siteUrl}/#organization` },
  inLanguage: locale.value === 'en' ? 'en-US' : 'bg-BG',
}), 'website');

const tiktokProfileUrl = 'https://www.tiktok.com/@r_3535978?_r=1&_t=ZN-98s9ZtXXZCw'

function openTikTok(event: MouseEvent) {
  if (!/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) return

  event.preventDefault()
  window.location.href = tiktokProfileUrl
}

const route = useRoute()
const hideSiteFooter = computed(() => route.meta.hideSiteFooter === true)

useHead(() => ({
  htmlAttrs: localeHead.value.htmlAttrs,
  title: t('pageTitle'),
  titleTemplate: '%s | vodi4ka.com',
  meta: [
    // Tell browsers not to auto-darken the page when the phone OS is in dark mode.
    { name: 'color-scheme', content: 'light' },
  ],
  link: [
    { rel: 'icon', type: 'image/png', href: '/favicon-32x32.png', sizes: '32x32' },
    { rel: 'icon', type: 'image/png', href: '/favicon-16x16.png', sizes: '16x16' },
    { rel: 'apple-touch-icon', href: '/apple-touch-icon.png', sizes: '180x180' },
    { rel: 'canonical', href: canonicalUrl.value },
    ...alternateLinks.value,
  ]
}));
</script>
<template>
  <Toast />
  <navbar/>
  <main class="content">
    <slot/>
  </main>
  <app-footer v-if="!hideSiteFooter" class="content mt-6"/>
  <div v-if="!hideSiteFooter" class="bottom-line">
    <div class="content content-padding">
        <h3 class="white bold">{{ t('footer.copyright') }}</h3>
        <ul>
            <li>
              <a href="https://www.facebook.com/vodi4ka.com1" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <img src="/icons/facebook.svg" alt="Facebook icon">
              </a>
            </li>
            <li>
              <a href="https://www.instagram.com/www_vodi4ka_com/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <img src="/icons/instagram.svg" alt="Instagram icon">
              </a>
            </li>
            <li>
              <a href="https://www.youtube.com/@vodi4ka_com" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                <img src="/icons/youtube.svg" alt="Youtube icon">
              </a>
            </li>
            <li>
              <a
                :href="tiktokProfileUrl"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TikTok"
                @click="openTikTok"
              >
                <img src="/icons/tiktok.svg" alt="TikTok icon">
              </a>

            </li>
        </ul>
    </div>
</div>
</template>

<style scoped>
.bottom-line {
    width: 100%;
    background-color: var(--accent-red);

    > div {
        height: 43px;
        display: flex;
        justify-content: space-between;
        align-items: center;

        img {
            max-height: 30px;
        }

        ul {
            display: flex;
            gap: 10px;
        }
    }

    @media (max-width: 768px) {
        height: 65px;
        padding-bottom: 1rem;
    }

}
</style>